import { useState, useCallback, useEffect } from 'react';
import { Flashlight, FlashlightOff, FileCog } from 'lucide-react';
import Navbar from './Navbar';
import { CardCreatorCanvas, ImageUploader, ImageCropper, ColorPicker, type CardColor, type WhiteTreatment, type EnvironmentPreset } from './components/CardCreator';
import './App.css';
import './CreatePage.css';

// Card aspect ratio (credit card)
const CARD_ASPECT_RATIO = 2.125 / 3.37;
const ASPECT_TOLERANCE = 0.02; // 2% tolerance

type CardSide = 'front' | 'back';

const DEFAULT_BG_COLOR = '#BCBCBC';
const DEFAULT_BACK_WHITE = '/ac_back_white.png';
const DEFAULT_BACK_BLACK = '/ac_back_black.png';

// Crop an image to card aspect ratio (center crop)
function cropImageToCardAspect(src: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const targetAspect = CARD_ASPECT_RATIO;
      const imgAspect = img.width / img.height;

      let cropWidth = img.width;
      let cropHeight = img.height;
      let cropX = 0;
      let cropY = 0;

      if (imgAspect > targetAspect) {
        // Image is wider than target - crop width
        cropWidth = img.height * targetAspect;
        cropX = (img.width - cropWidth) / 2;
      } else {
        // Image is taller than target - crop height
        cropHeight = img.width / targetAspect;
        cropY = (img.height - cropHeight) / 2;
      }

      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        resolve(canvas.toDataURL('image/png'));
      } else {
        resolve(src);
      }
    };
    img.src = src;
  });
}

export default function CreatePage() {
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [editingSide, setEditingSide] = useState<CardSide>('front');
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_BG_COLOR);
  const [cardColor, setCardColor] = useState<CardColor>('white');
  const [whiteTreatment, setWhiteTreatment] = useState<WhiteTreatment>('glossy');
  const [isSpotUv, setIsSpotUv] = useState(false);
  const [environment, setEnvironment] = useState<EnvironmentPreset>('apartment');
  const [croppedDefaultWhite, setCroppedDefaultWhite] = useState<string | null>(null);
  const [croppedDefaultBlack, setCroppedDefaultBlack] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(10);
  const [activeTab, setActiveTab] = useState<'creator' | 'checkout'>('creator');
  const [shippingRegion, setShippingRegion] = useState<'montreal' | 'quebec' | 'canada' | 'us' | 'international'>('canada');
  const [showCover, setShowCover] = useState(false);
  const [envPanelExpanded, setEnvPanelExpanded] = useState(true);

  // Pricing logic
  const isDoubleSided = backImage !== null;
  const singleSidedPrice = 4.50;
  const doubleSidedPrice = 5.00;
  const earlyAdopterDiscount = 0.50; // 50% off

  const getBulkDiscount = (qty: number) => {
    if (qty >= 50) return 0.20;
    return 0;
  };

  const bulkDiscount = getBulkDiscount(quantity);
  const perCardBase = isDoubleSided ? doubleSidedPrice : singleSidedPrice;
  const subtotal = perCardBase * quantity;
  const bulkSavings = subtotal * bulkDiscount;
  const afterBulk = subtotal - bulkSavings;
  const earlyAdopterSavings = afterBulk * earlyAdopterDiscount;
  const afterDiscount = afterBulk - earlyAdopterSavings;

  // Shipping calculation (~6g per card, shipping from Montreal)
  // Small packets are cheap - base rate covers most small orders
  const getShippingCost = (qty: number, region: typeof shippingRegion) => {
    const weightGrams = qty * 6;

    switch (region) {
      case 'montreal':
        return 0; // Free local pickup/delivery
      case 'quebec':
        // Lettermail/small packet within Quebec
        if (weightGrams <= 100) return 3;
        if (weightGrams <= 200) return 4;
        return 5;
      case 'canada':
        // Canada Post small packet
        if (weightGrams <= 100) return 4;
        if (weightGrams <= 200) return 5;
        if (weightGrams <= 500) return 7;
        return 9;
      case 'us':
        // Small packet to US
        if (weightGrams <= 100) return 6;
        if (weightGrams <= 200) return 8;
        if (weightGrams <= 500) return 10;
        return 14;
      case 'international':
        // International small packet
        if (weightGrams <= 100) return 10;
        if (weightGrams <= 200) return 14;
        if (weightGrams <= 500) return 18;
        return 25;
      default:
        return 5;
    }
  };

  const shippingCost = getShippingCost(quantity, shippingRegion);
  const totalPrice = afterDiscount + shippingCost;

  // Crop default back images on mount
  useEffect(() => {
    cropImageToCardAspect(DEFAULT_BACK_WHITE).then(setCroppedDefaultWhite);
    cropImageToCardAspect(DEFAULT_BACK_BLACK).then(setCroppedDefaultBlack);
  }, []);

  const handleImageSelect = useCallback((side: CardSide) => (file: File, dataUrl: string) => {
    setEditingSide(side);
    // Check aspect ratio
    const img = new Image();
    img.onload = () => {
      const imageAspect = img.width / img.height;
      const targetAspect = CARD_ASPECT_RATIO;

      if (Math.abs(imageAspect - targetAspect) < ASPECT_TOLERANCE) {
        // Close enough, use directly
        if (side === 'front') {
          setFrontImage(dataUrl);
        } else {
          setBackImage(dataUrl);
        }
        setUploadedImage(null);
        setShowCropper(false);
      } else {
        // Need cropping
        setUploadedImage(dataUrl);
        setShowCropper(true);
      }
    };
    img.src = dataUrl;
  }, []);

  const handleCropComplete = useCallback((croppedDataUrl: string) => {
    if (editingSide === 'front') {
      setFrontImage(croppedDataUrl);
    } else {
      setBackImage(croppedDataUrl);
    }
    setUploadedImage(null);
    setShowCropper(false);
  }, [editingSide]);

  const handleCropCancel = useCallback(() => {
    setUploadedImage(null);
    setShowCropper(false);
  }, []);

  const handleReset = useCallback((side: CardSide) => () => {
    if (side === 'front') {
      setFrontImage(null);
    } else {
      setBackImage(null);
    }
  }, []);

  return (
    <div className="create-page">
      <Navbar />

      <div className="create-canvas">
        <CardCreatorCanvas
          frontImageUrl={frontImage || undefined}
          backImageUrl={backImage || (cardColor === 'black' ? croppedDefaultBlack : croppedDefaultWhite) || undefined}
          cardColor={cardColor}
          whiteTreatment={whiteTreatment}
          showCover={showCover}
          backgroundColor={backgroundColor}
          environment={environment}
        />
      </div>

      <div className={`create-sidebar-left ${envPanelExpanded ? '' : 'collapsed'}`}>
        {!envPanelExpanded ? (
          <button
            className="create-env-header"
            onClick={() => setEnvPanelExpanded(true)}
          >
            <span>Environment</span>
            <svg
              className="create-env-chevron"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        ) : (
          <>
            <button
              className="create-env-collapse-btn"
              onClick={() => setEnvPanelExpanded(false)}
              aria-label="Collapse panel"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>

            <div className="create-section">
              <h3 className="create-side-label">Background</h3>
              <ColorPicker value={backgroundColor} onChange={setBackgroundColor} />
            </div>

            <div className="create-section">
              <h3 className="create-side-label">Lighting</h3>
              <select
                className="create-select"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as EnvironmentPreset)}
              >
                <option value="apartment">Apartment</option>
                <option value="city">City</option>
                <option value="dawn">Dawn</option>
                <option value="forest">Forest</option>
                <option value="lobby">Lobby</option>
                <option value="night">Night</option>
                <option value="park">Park</option>
                <option value="studio">Studio</option>
                <option value="sunset">Sunset</option>
                <option value="warehouse">Warehouse</option>
              </select>
            </div>

            <div className="create-section-inline">
              <h3 className="create-side-label">Sleeve</h3>
              <button
                className={`create-sleeve-toggle ${showCover ? 'active' : ''}`}
                onClick={() => setShowCover(!showCover)}
              >
                <span className="create-sleeve-toggle-track">
                  <span className="create-sleeve-toggle-thumb" />
                </span>
              </button>
            </div>
          </>
        )}
      </div>

      <div className="create-sidebar">
        <div className="create-tabs">
          <button
            className={`create-tab ${activeTab === 'creator' ? 'active' : ''}`}
            onClick={() => setActiveTab('creator')}
          >
            Customize
          </button>
          <button
            className={`create-tab ${activeTab === 'checkout' ? 'active' : ''}`}
            onClick={() => setActiveTab('checkout')}
          >
            Pricing
          </button>
        </div>

        {activeTab === 'creator' && (
          <>
            <p className="create-subtitle">
              Upload artwork for the front and back of your album card.
            </p>

            <div className="create-sides">
          {/* Front side */}
          <div className="create-side">
            <h3 className="create-side-label">Front</h3>
            {frontImage ? (
              <div className="create-thumbnail-wrapper">
                <div className="create-thumbnail">
                  <img src={frontImage} alt="Front artwork" />
                </div>
                <div className="create-thumbnail-overlay" onClick={handleReset('front')}>
                  <span>Replace</span>
                </div>
              </div>
            ) : (
              <ImageUploader onImageSelect={handleImageSelect('front')} thumbnailMode />
            )}
          </div>

          {/* Back side */}
          <div className="create-side">
            <h3 className="create-side-label">Back</h3>
            {backImage ? (
              <div className="create-thumbnail-wrapper">
                <div className="create-thumbnail">
                  <img src={backImage} alt="Back artwork" />
                </div>
                <div className="create-thumbnail-overlay" onClick={handleReset('back')}>
                  <span>Replace</span>
                </div>
              </div>
            ) : (
              <div className="create-thumbnail-wrapper">
                <div className="create-thumbnail create-thumbnail-default">
                  <img src={(cardColor === 'black' ? croppedDefaultBlack : croppedDefaultWhite) || ''} alt="Default back" />
                </div>
                <div className="create-thumbnail-overlay" onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/png,image/jpeg,image/webp';
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const dataUrl = ev.target?.result as string;
                        handleImageSelect('back')(file, dataUrl);
                      };
                      reader.readAsDataURL(file);
                    }
                  };
                  input.click();
                }}>
                  <span>Upload</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="create-section">
          <h3 className="create-side-label">Card Color</h3>
          <div className="create-card-color-picker">
            <button
              className={`create-card-color-option ${cardColor === 'white' ? 'active' : ''}`}
              onClick={() => setCardColor('white')}
            >
              <div className="create-card-color-swatch create-card-color-white" />
              <span>White</span>
            </button>
            <button
              className={`create-card-color-option ${cardColor === 'black' ? 'active' : ''}`}
              onClick={() => setCardColor('black')}
            >
              <div className="create-card-color-swatch create-card-color-black" />
              <span>Black</span>
            </button>
          </div>
        </div>

        <div className="create-section">
          <h3 className="create-side-label">{cardColor === 'white' ? 'White Treatment' : 'Black Treatment'}</h3>
          <div className="create-treatment-picker">
            <button
              className={`create-treatment-option ${!isSpotUv && whiteTreatment === 'glossy' ? 'active' : ''}`}
              onClick={() => { setWhiteTreatment('glossy'); setIsSpotUv(false); }}
            >
              <Flashlight size={20} />
              <span>Glossy</span>
            </button>
            <button
              className={`create-treatment-option ${!isSpotUv && whiteTreatment === 'matte' ? 'active' : ''}`}
              onClick={() => { setWhiteTreatment('matte'); setIsSpotUv(false); }}
            >
              <FlashlightOff size={20} />
              <span>Matte</span>
            </button>
            <button
              className={`create-treatment-option ${isSpotUv ? 'active' : ''}`}
              onClick={() => setIsSpotUv(true)}
            >
              <FileCog size={20} />
              <span>Spot UV</span>
            </button>
          </div>
          {isSpotUv ? (
            <>
              <p className="create-treatment-description">
                Coming soon: Upload a mask to define custom glossy areas
              </p>
              <button className="create-spot-uv-button" disabled>
                <span className="create-spot-uv-icon">+</span>
                <span>Upload mask</span>
              </button>
            </>
          ) : (
            <p className="create-treatment-description">
              {whiteTreatment === 'glossy'
                ? (cardColor === 'white'
                    ? 'White areas show shiny plastic surface'
                    : 'Black areas show shiny plastic surface')
                : (cardColor === 'white'
                    ? 'White ink printed for a matte finish'
                    : 'Black ink printed for a matte finish')}
            </p>
          )}
        </div>

        <div className="create-info">
          <h3>Card Specifications</h3>
          <ul>
            <li>Album card size (3.37" x 2.125")</li>
            <li>NFC enabled for music playback</li>
            <li>Printed at our Montreal shop</li>
          </ul>
        </div>
          </>
        )}

        {activeTab === 'checkout' && (
          <>
            <p className="create-subtitle">
              Review your order and get a price estimate.
            </p>

            <div className="create-pricing">
              <h3 className="create-side-label">Quantity</h3>
              <div className="create-quantity-row">
                <div className="create-quantity-presets">
                  {[10, 25, 50, 100].map((qty) => (
                    <button
                      key={qty}
                      className={`create-quantity-preset ${quantity === qty ? 'active' : ''}`}
                      onClick={() => setQuantity(qty)}
                    >
                      {qty}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  className={`create-quantity-input ${quantity < 10 ? 'invalid' : ''}`}
                  value={quantity || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setQuantity(val === '' ? 0 : parseInt(val));
                  }}
                />
              </div>

              {quantity < 10 ? (
                <p className="create-minimum-notice">Minimum order quantity is 10 cards.</p>
              ) : (
                <>
                  <div className="create-price-breakdown">
                    <div className="create-price-row">
                      <span>{isDoubleSided ? 'Double-sided' : 'Single-sided'}</span>
                      <span>${perCardBase.toFixed(2)}/card</span>
                    </div>
                    <div className="create-price-row">
                      <span>Subtotal ({quantity} cards)</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    {bulkDiscount > 0 && (
                      <div className="create-price-row create-price-discount">
                        <span>Bulk discount ({(bulkDiscount * 100).toFixed(0)}% off)</span>
                        <span>-${bulkSavings.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="create-early-adopter">
                    <span className="create-early-adopter-label">Early Adopter</span>
                    <span className="create-early-adopter-value">-${earlyAdopterSavings.toFixed(2)}</span>
                    <span className="create-early-adopter-badge">50% OFF</span>
                  </div>

                  <h3 className="create-side-label" style={{ marginTop: '16px' }}>Shipping</h3>
                  <select
                    className="create-select"
                    value={shippingRegion}
                    onChange={(e) => setShippingRegion(e.target.value as typeof shippingRegion)}
                  >
                    <option value="montreal">Montreal (Free)</option>
                    <option value="quebec">Quebec</option>
                    <option value="canada">Rest of Canada</option>
                    <option value="us">United States</option>
                    <option value="international">International</option>
                  </select>
                  <div className="create-price-row" style={{ marginTop: '8px' }}>
                    <span>Shipping</span>
                    <span>{shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}</span>
                  </div>

                  <div className="create-total">
                    <span>Estimated total</span>
                    <span className="create-total-value">${totalPrice.toFixed(2)}</span>
                  </div>
                  <p className="create-pricing-note">Limited time early adopter pricing.</p>
                  <button className="create-request-order-btn">Request Order</button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showCropper && uploadedImage && (
        <ImageCropper
          imageUrl={uploadedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
